import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { FuturesPaperEntryRequest, FuturesPaperPreview } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import AdminLayout from '@/components/Layout/AdminLayout';
import FuturesPaperOpeningRecovery from './FuturesPaperOpeningRecovery';

const money = (value: number) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const time = (value: number | null) => (value === null ? 'Unavailable' : new Date(value * 1000).toLocaleString());

export default function FuturesPaper() {
    const [search, setSearch] = useSearchParams();
    const [portfolioId, setPortfolioId] = useState('');
    const [contractId, setContractId] = useState('');
    const [entryLimit, setEntryLimit] = useState('');
    const [closeLimit, setCloseLimit] = useState('');
    const [capitalCeiling, setCapitalCeiling] = useState('');
    const [closingLimit, setClosingLimit] = useState('');
    const [review, setReview] = useState<{ request: FuturesPaperEntryRequest; preview: FuturesPaperPreview } | null>(null);
    const [acknowledged, setAcknowledged] = useState(false);
    const [error, setError] = useState('');
    const options = useQuery(trpc.admin.getFuturesPaperOptions.queryOptions());
    const outstanding = useQuery(trpc.admin.listFuturesPaperPositions.queryOptions(undefined, { refetchInterval: 5000 }));
    const positionId = Number(search.get('positionId') ?? 0);
    const position = useQuery(
        trpc.admin.getFuturesPaperPosition.queryOptions({ positionId }, { enabled: positionId > 0, refetchInterval: 5000 })
    );
    const preview = useMutation(trpc.admin.previewFuturesPaperEntry.mutationOptions({ retry: false }));
    const submit = useMutation(trpc.admin.submitFuturesPaperEntry.mutationOptions({ retry: false }));
    const close = useMutation(trpc.admin.closeFuturesPaperPosition.mutationOptions({ retry: false }));
    const cancel = useMutation(trpc.admin.cancelFuturesPaperOrder.mutationOptions({ retry: false }));
    const invalidateReview = () => {
        setReview(null);
        setAcknowledged(false);
        setError('');
    };
    const report = (cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause));
    const busy = preview.isPending || submit.isPending || close.isPending || cancel.isPending;
    const p = position.data;

    return (
        <AdminLayout>
            <main className="mx-auto max-w-4xl space-y-5 p-6 text-slate-200">
                <h1 className="text-2xl font-semibold">Supervised MES paper trade</h1>
                <p>
                    One long dated contract on a dedicated TradeStation futures SIM account. Review the limits and margin before submitting.
                    A protective limit close is attempted within five minutes, before the regular session ends. A limit order can remain
                    unfilled; stay present until the broker is flat.
                </p>
                {(error || options.error || outstanding.error || position.error) && (
                    <p role="alert" className="rounded border border-red-700 p-3 text-red-300">
                        {error || options.error?.message || outstanding.error?.message || position.error?.message}
                    </p>
                )}
                {outstanding.data && outstanding.data.length > 0 && (
                    <section className="rounded border border-amber-700 p-4">
                        <h2 className="font-semibold">Unresolved paper positions</h2>
                        {outstanding.data.map((item) => (
                            <button
                                key={item.positionId}
                                className="mr-3 underline"
                                onClick={() => setSearch({ positionId: String(item.positionId) })}
                            >
                                {item.brokerSymbol} · {item.submissionState}
                            </button>
                        ))}
                        <p className="mt-2 text-sm">
                            An unresolved acknowledgement keeps the account reserved. Inspect the original order; do not submit another
                            entry.
                        </p>
                    </section>
                )}
                <form
                    className="grid gap-4 rounded border border-slate-700 p-5 sm:grid-cols-2"
                    onSubmit={async (event) => {
                        event.preventDefault();
                        invalidateReview();
                        const request = {
                            portfolioId: Number(portfolioId),
                            contractId: Number(contractId),
                            entryLimit: Number(entryLimit),
                            closeLimit: Number(closeLimit),
                            capitalCeiling: Number(capitalCeiling)
                        };
                        try {
                            setReview({ request, preview: await preview.mutateAsync(request) });
                        } catch (cause) {
                            report(cause);
                        }
                    }}
                >
                    <label>
                        Dedicated paper portfolio
                        <select
                            className="mt-1 w-full rounded bg-slate-900 p-2"
                            value={portfolioId}
                            required
                            onChange={(e) => {
                                setPortfolioId(e.target.value);
                                invalidateReview();
                            }}
                        >
                            <option value="">Choose portfolio</option>
                            {options.data?.portfolios.map((v) => (
                                <option key={v.id} value={v.id}>
                                    {v.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Dated MES contract
                        <select
                            className="mt-1 w-full rounded bg-slate-900 p-2"
                            value={contractId}
                            required
                            onChange={(e) => {
                                setContractId(e.target.value);
                                invalidateReview();
                            }}
                        >
                            <option value="">Choose contract</option>
                            {options.data?.contracts.map((v) => (
                                <option key={v.id} value={v.id}>
                                    {v.canonicalSymbol} · last trade {time(v.lastTradingTimestamp)}
                                </option>
                            ))}
                        </select>
                    </label>
                    {[
                        { label: 'Entry buy limit', value: entryLimit, set: setEntryLimit, step: '0.25' },
                        { label: 'Protective sell limit', value: closeLimit, set: setCloseLimit, step: '0.25' },
                        { label: 'Margin + entry commission ceiling (USD)', value: capitalCeiling, set: setCapitalCeiling, step: '0.01' }
                    ].map((field) => (
                        <label key={field.label}>
                            {field.label}
                            <input
                                className="mt-1 w-full rounded bg-slate-900 p-2"
                                type="number"
                                required
                                min={field.step}
                                step={field.step}
                                value={field.value}
                                onChange={(e) => {
                                    field.set(e.target.value);
                                    invalidateReview();
                                }}
                            />
                        </label>
                    ))}
                    <button className="rounded bg-blue-700 px-4 py-2 disabled:opacity-40" disabled={busy || !!outstanding.data?.length}>
                        Preview paper entry
                    </button>
                    {options.data?.portfolios.length === 0 && (
                        <p className="text-sm sm:col-span-2">
                            Import the separate futures SIM account through Trading Accounts and link an empty paper portfolio. Keep
                            existing stock and option portfolios on their current accounts.
                        </p>
                    )}
                </form>
                {review && (
                    <section className="space-y-3 rounded border border-blue-700 p-5">
                        <h2 className="font-semibold">Review one {review.preview.brokerSymbol} contract</h2>
                        <p>
                            Buy limit {review.request.entryLimit}; protective sell limit {review.request.closeLimit}. Broker initial margin{' '}
                            {money(review.preview.initialMargin)}; estimated entry commission {money(review.preview.estimatedCommission)};
                            ceiling {money(review.request.capitalCeiling)}.
                        </p>
                        <p>
                            Last traded {review.preview.lastTradePrice} at {time(review.preview.sourceTradeTimestamp)}. Bid/ask quote time
                            is unavailable. Tick 0.25; point value $5. The server refreshes all checks before submission.
                        </p>
                        <p>Preview close deadline: {time(review.preview.closeDeadline)}. Orders use immediate-or-cancel limits.</p>
                        <label className="block">
                            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} /> I will
                            supervise this one-contract paper trade through broker-flat confirmation.
                        </label>
                        <button
                            className="rounded bg-blue-700 px-4 py-2 disabled:opacity-40"
                            disabled={!acknowledged || busy || !!outstanding.data?.length}
                            onClick={async () => {
                                try {
                                    const result = await submit.mutateAsync({
                                        ...review.request,
                                        expectedInstrumentKey: review.preview.instrumentKey,
                                        acknowledgeOneContractPaperOrder: true
                                    });
                                    setSearch({ positionId: String(result.positionId) });
                                    setReview(null);
                                    setAcknowledged(false);
                                } catch (cause) {
                                    report(cause);
                                    setReview(null);
                                }
                                await outstanding.refetch();
                            }}
                        >
                            Submit one MES contract on paper
                        </button>
                    </section>
                )}
                {p && (
                    <section className="space-y-3 rounded border border-slate-700 p-5">
                        <h2 className="font-semibold">
                            {p.brokerSymbol} · position {p.positionId}
                        </h2>
                        <p>
                            Submission: {p.submissionState}. Quantity: {p.quantity} contract; pending: {p.pendingQuantity}. Broker flat:{' '}
                            {time(p.brokerFlatTimestamp)}.
                        </p>
                        <dl className="grid grid-cols-2 gap-3">
                            {[
                                ['Cash change', money(p.cashChange)],
                                ['Realized gross P&L', money(p.realizedGrossPnl)],
                                ['Actual fees', money(p.totalFees)],
                                ['Unrealized P&L', money(p.unrealizedPnl)],
                                ['Notional exposure', money(p.notionalExposure)],
                                ['Reserved initial margin', money(p.reservedMargin)],
                                ['Last mark source', time(p.markSourceTimestamp)],
                                ['Close deadline', time(p.closeDeadline)]
                            ].map(([label, value]) => (
                                <div key={label}>
                                    <dt className="text-sm text-slate-400">{label}</dt>
                                    <dd>{value}</dd>
                                </div>
                            ))}
                        </dl>
                        {p.lastError && (
                            <p role="alert" className="text-amber-300">
                                {p.lastError}
                            </p>
                        )}
                        {p.providerOrderId === null && ['submitting', 'uncertain'].includes(p.submissionState) && (
                            <FuturesPaperOpeningRecovery
                                position={p}
                                onApplied={async () => {
                                    await position.refetch();
                                    await outstanding.refetch();
                                }}
                            />
                        )}
                        {p.closingSubmissionHeld && (
                            <p className="text-amber-300">Closing acknowledgement needs reconciliation. Another close remains blocked.</p>
                        )}
                        {p.orders.map((order) => (
                            <p key={order.id}>
                                {order.side} order {order.id}: {order.status}; {order.quantityFilled} filled.
                                {['open', 'replacing'].includes(order.status) && (
                                    <button
                                        disabled={busy}
                                        className="ml-3 underline"
                                        onClick={async () => {
                                            try {
                                                await cancel.mutateAsync({ positionId: p.positionId, orderId: order.id });
                                                await position.refetch();
                                            } catch (cause) {
                                                report(cause);
                                            }
                                        }}
                                    >
                                        Request cancellation
                                    </button>
                                )}
                            </p>
                        ))}
                        {p.quantity > 0 && (
                            <form
                                className="flex items-end gap-3"
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    setError('');
                                    try {
                                        await close.mutateAsync({
                                            positionId: p.positionId,
                                            limitPrice: Number(closingLimit),
                                            acknowledgePaperClose: true
                                        });
                                        await position.refetch();
                                    } catch (cause) {
                                        report(cause);
                                    }
                                }}
                            >
                                <label>
                                    Explicit sell limit
                                    <input
                                        className="block rounded bg-slate-900 p-2"
                                        type="number"
                                        step="0.25"
                                        min="0.25"
                                        required
                                        value={closingLimit}
                                        onChange={(e) => setClosingLimit(e.target.value)}
                                    />
                                </label>
                                <button
                                    className="rounded bg-amber-700 px-4 py-2 disabled:opacity-40"
                                    disabled={
                                        busy ||
                                        p.pendingQuantity > 0 ||
                                        p.closingSubmissionHeld ||
                                        p.orders.some((o) => ['open', 'replacing'].includes(o.status))
                                    }
                                >
                                    Close one contract on paper
                                </button>
                            </form>
                        )}
                    </section>
                )}
            </main>
        </AdminLayout>
    );
}
